import i18n from 'i18next'
import PageOnboarding from '@/components/page-onboarding.tsx'
import { useEffect, useState } from 'react'
import useGlobalStore from '@/useGlobalStore.ts'
import usePreferences from '@/usePreferences.ts'
import App from '@/App.tsx'
import * as Sentry from '@sentry/react'
import { Network } from '@capacitor/network'
import { App as CapacitorApp } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { Capacitor } from '@capacitor/core'
import OfflineBanner from '@/components/offline-banner.tsx'
import BlockMobilesOnWebScreen from '@/components/block-mobiles-on-web-screen.tsx'

const DevErrorBoundary = ({ children }: any) => {
	if (import.meta.env.VITE_ENVIRONMENT !== 'production') {
		return children
	}

	return (
		<Sentry.ErrorBoundary
			fallback={
				<div className="flex flex-col h-full w-full justify-center items-center">
					<div className="font-bold">An error has occurred :(</div>
					<div className="text-sm mt-3">We will look into it quickly</div>
				</div>
			}
			showDialog>
			{children}
		</Sentry.ErrorBoundary>
	)
}

const AppContainer = () => {
	const {
		availableAppLanguages,
		appLanguageCode,
		changeAppLanguage,
		learningLanguageCode,
		changeLearningLanguage,
		defaultAppLanguageCode,
		setIsNetworkOnline,
		setAppVersion,
		setTips,
	} = useGlobalStore()
	const { getItem: getPreferenceItem } = usePreferences()
	const [appLanguageLoadedFromPreferences, setAppLanguageLoadedFromPreferences] = useState(false)
	const [learningLanguageLoadedFromPreferences, setLearningLanguageLoadedFromPreferences] = useState(false)
	const shouldBlockMobileOnWeb = false

	const onPreferencesLoaded = () => {
		setAppLanguageLoadedFromPreferences(true)
		setLearningLanguageLoadedFromPreferences(true)
	}

	useEffect(() => {
		if (appLanguageCode !== '') {
			i18n.changeLanguage(appLanguageCode).then()
		}
	}, [appLanguageCode])

	const getRandomizedTips = () => {
		return [
			{ title: 'Ask for Translations', text: 'Need a quick translation? Just ask me!' },
			{ title: 'Sentence Corrections', text: 'Type a sentence, and I’ll correct it for you!' },
			{ title: 'Grammar Help', text: 'Confused about grammar? Ask me to explain!' },
			{ title: 'Word Usage', text: 'Not sure how to use a word? I can give you examples!' },
			{ title: 'Practice Conversations', text: 'Chat with me in your target language for practice!' },
			{ title: 'Pronunciation Tips', text: 'Ask me how to pronounce a word correctly!' },
			{ title: 'Flashcard Suggestions', text: 'Need words to practice? I can suggest some!' },
			{ title: 'Explain Idioms', text: 'Want to learn an idiom? I’ll break it down for you!' },
		].sort(() => Math.random() - 0.5)
	}

	// const handleLiveUpdate = async () => {
	// 	const result = await LiveUpdates.sync()
	// 	if (result.activeApplicationPathChanged) {
	// 		await LiveUpdates.reload()
	// 	} else {
	// 		await SplashScreen.hide()
	// 	}
	// }

	useEffect(() => {
		if (Capacitor.getPlatform() !== 'web') {
			CapacitorApp.getInfo().then((info) => {
				setAppVersion(`v${info.version}#${info.build}`)
			})

			// handleLiveUpdate().then(() => {
			// 	console.log('Live update completed')
			// })
		}

		setTips(getRandomizedTips())

		getPreferenceItem('appLanguageCode').then((value) => {
			if (value !== null) {
				changeAppLanguage(value).then()
				setAppLanguageLoadedFromPreferences(true)
			} else {
				Device.getLanguageTag()
					.then((deviceLanguageCode) => {
						if (availableAppLanguages[deviceLanguageCode.value] !== undefined) {
							changeAppLanguage(deviceLanguageCode.value).then()
						} else {
							changeAppLanguage(defaultAppLanguageCode).then()
						}
					})
					.catch(() => changeAppLanguage(defaultAppLanguageCode).then())
			}
		})

		getPreferenceItem('learningLanguageCode').then((value) => {
			if (value !== null) {
				changeLearningLanguage(value).then()
				setLearningLanguageLoadedFromPreferences(true)
			}
		})

		return () => {
			CapacitorApp.removeAllListeners().then()
		}
	}, [])

	useEffect(() => {
		Network.getStatus().then((status) => {
			setIsNetworkOnline(status.connected)
		})

		Network.addListener('networkStatusChange', (status) => {
			setIsNetworkOnline(status.connected)
		}).then()

		return () => {
			Network.removeAllListeners().then()
		}
	}, [setIsNetworkOnline])

	// Workaround for iOS Catalyst app not receiving window size change events
	// useEffect(() => {
	// 	// @ts-ignore
	// 	if (Capacitor.getPlatform() === 'ios' && window.isMacCatalystApp) {
	// 		window.dispatchEvent(new Event('resize'))
	//
	// 		const handleResize = () => {
	// 			setIsNetworkOnline(false)
	// 			// @ts-ignore
	// 			dummyInput?.current?.focus()
	// 			setTimeout(() => {
	// 				// @ts-ignore
	// 				dummyInput?.current?.blur()
	// 				setIsNetworkOnline(true)
	// 			}, 800)
	// 		}
	//
	// 		window.addEventListener('resize', handleResize)
	//
	// 		return () => {
	// 			window.removeEventListener('resize', handleResize)
	// 		}
	// 	}
	// }, [dummyInput])

	// useEffect(() => {
	// 	// Define handles for each listener
	// 	let registrationListener: PluginListenerHandle
	// 	let registrationErrorListener: PluginListenerHandle
	// 	let pushNotificationReceivedListener: PluginListenerHandle
	// 	let pushNotificationActionPerformedListener: PluginListenerHandle
	//
	// 	const initPushNotifications = async () => {
	// 		// 1. Request permissions
	// 		const result = await PushNotifications.requestPermissions()
	// 		if (result.receive === 'granted') {
	// 			await PushNotifications.register()
	// 		} else {
	// 			console.log('Push notifications permission not granted')
	// 		}
	//
	// 		// 2. Add listeners and await the listener handles
	// 		registrationListener = await PushNotifications.addListener('registration', (token) => {
	// 			console.log('Push registration success, token:', token.value)
	// 			// Optionally, send token to your server
	// 		})
	//
	// 		registrationErrorListener = await PushNotifications.addListener('registrationError', (error) => {
	// 			console.error('Error on registration:', error)
	// 		})
	//
	// 		pushNotificationReceivedListener = await PushNotifications.addListener(
	// 			'pushNotificationReceived',
	// 			(notification) => {
	// 				console.log('Push notification received:', notification)
	// 				// Handle foreground notification
	// 			},
	// 		)
	//
	// 		pushNotificationActionPerformedListener = await PushNotifications.addListener(
	// 			'pushNotificationActionPerformed',
	// 			(notification) => {
	// 				console.log('Push notification action performed:', notification)
	// 				// Handle notification tap
	// 			},
	// 		)
	// 	}
	//
	// 	initPushNotifications()
	//
	// 	// Cleanup: Remove listeners on component unmount
	// 	return () => {
	// 		if (registrationListener) {
	// 			registrationListener.remove()
	// 		}
	// 		if (registrationErrorListener) {
	// 			registrationErrorListener.remove()
	// 		}
	// 		if (pushNotificationReceivedListener) {
	// 			pushNotificationReceivedListener.remove()
	// 		}
	// 		if (pushNotificationActionPerformedListener) {
	// 			pushNotificationActionPerformedListener.remove()
	// 		}
	// 	}
	// }, [])

	return (
		<DevErrorBoundary>
			{shouldBlockMobileOnWeb ? (
				<BlockMobilesOnWebScreen />
			) : (
				<div dir={availableAppLanguages[appLanguageCode]?.dir}>
					{appLanguageCode !== null &&
					appLanguageCode !== '' &&
					learningLanguageCode !== null &&
					learningLanguageCode !== '' &&
					appLanguageLoadedFromPreferences &&
					learningLanguageLoadedFromPreferences ? (
						<App />
					) : (
						<>
							<OfflineBanner className="fixed top-safe inset-x-0 z-50" />
							<PageOnboarding
								setPreferencesLoaded={onPreferencesLoaded}
								preferencesLoaded={appLanguageLoadedFromPreferences}
							/>
						</>
					)}
				</div>
			)}
		</DevErrorBoundary>
	)
}

export default AppContainer
